import { PropsOf } from '@emotion/react'
import {
  Slide,
  Dialog,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Button,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Box,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Stack,
  TextareaAutosize,
  FormControl,
  InputLabel,
  Divider,
} from '@mui/material'
import { TransitionProps } from '@mui/material/transitions'
import { forwardRef, Ref, useEffect, useRef } from 'react'
import CloseIcon from '@mui/icons-material/Close'
import { HTMLInputTypeAttribute, useState } from 'react'
import { useFormik, Formik, Form, Field } from 'formik'
import * as yup from 'yup'
import moment from 'moment'
import { CurrencyFormatInput, CurrencyFormatInput2 } from '@sendhub/shared-ui'
import { format } from 'react-number-format/types/numeric_format'
import { NumericFormat } from 'react-number-format'
import lodash from 'lodash'
import { integrationsApi } from '../services/integrations-api'

const COMMON_INTEGRATION_TYPES = [
  'Sync Contacts',
  'Sync Messages',
  'Send Messages',
  'Chat Bot',
  'Email Notifications for New Messages',
  'Other',
]
const APP_HAS_API_OPTIONS = ['Yes', `Yes, but the docs aren't hosted online`, 'No', `I don't know`]

const addHttpsTransform = (value: any, originalValue: any) =>
  originalValue.match(/^https:\/\/?/) ? originalValue : 'https://' + originalValue

// Define the validation schema using yup
const validationSchema = yup.object().shape({
  // Contact info
  firstName: yup.string().required('Required').label('First Name'),
  lastName: yup.string().required('Required').label('Last Name'),
  email: yup.string().required('Required').email('Invalid email').label('Email'),
  phone: yup.string().label('Phone'),
  companyName: yup.string().required('Required').label('Company Name'),
  position: yup.string().required('Required').label('Position').meta({ helperText: `What is your role in your company?` }),

  // Integration Details
  appName: yup
    .string()
    .required('Required')
    .label(`Name of 3rd-Party Service`)
    .meta({ helperText: `Please enter the full name of the service you'd like us to integrate with.` }),
  appDescription: yup.string().required('Required').label(`Short Description of 3rd-Party Service`),
  appWebsite: yup.string().required('Required').url('Invalid URL').transform(addHttpsTransform).label(`What's their website?`),
  appHasApi: yup.string().required().oneOf(APP_HAS_API_OPTIONS, 'Invalid Value').label(`Does the 3rd-party service have an API?`),
  appApiDocumentation: yup
    .string()
    .url('Invalid URL')
    .transform(addHttpsTransform)
    .label('API Documentation URL')
    .when('appHasApi', {
      is: `Yes`,
      then: (schema) => schema.required('Required'),
    }),

  integrationType: yup.string().required('Required').oneOf(COMMON_INTEGRATION_TYPES).label('Integration Type'),
  integrationDetails: yup
    .string()
    .required('Required')
    .label(`Integration Details`)
    .meta({ helperText: 'Briefly explain how you would like to use this service with SendHub and what features you need it to have.' }),
  desiredTimeline: yup
    .date()
    .min(new Date(), 'Must be a future date')
    // .default(new Date(moment().add('days', 30).toDate()).toISOString().substr(0, 10))
    .required('Required')
    .label('Desired Timeline')
    .meta({
      helperText: `Please let us know your desired timeline for this integration. This will help us determine the feasibility of your request and ensure that we can meet your needs within the desired timeframe.`,
    }),
  budget: yup.number().required('Required').label('Budget').meta({
    helperText: `Specify your budget for the integration. This helps us assess feasibility and deliver the best value for your investment.`,
  }),
  additionalComments: yup.string().label('Additional Comments'),
})
// const initialValues = validationSchema.cast({}, { assert: false,  })
const initialValues: yup.InferType<typeof validationSchema> = {
  firstName: ``,
  lastName: ``,
  email: ``,
  phone: ``,
  companyName: ``,
  position: ``,
  appName: ``,
  appDescription: ``,
  appWebsite: ``,
  appHasApi: ``,
  appApiDocumentation: ``,
  integrationType: ``,
  integrationDetails: ``,
  desiredTimeline: new Date(moment().add('days', 30).toDate()).toISOString().substr(0, 10) as unknown as Date,
  budget: '' as unknown as number,
  additionalComments: ``,
}
type FieldName = keyof typeof validationSchema.fields
const fields = validationSchema.describe().fields as Record<
  FieldName,
  yup.SchemaDescription | yup.SchemaObjectDescription | yup.SchemaInnerTypeDescription
>
function getFieldLabel(fieldName: FieldName): React.ReactNode {
  const field = fields[fieldName]
  return field?.label || ''
}

function getFieldRequired(fieldName: FieldName): boolean {
  const field = fields[fieldName]
  return !field.optional
}

function getFieldType(fieldName: FieldName): HTMLInputTypeAttribute {
  const field = fields[fieldName]
  return field.type
}

const Transition = forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement
  },
  ref: Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />
})

export interface RequestNewIntegrationFormDialogProps extends PropsOf<typeof Dialog> {
  close: () => void
}

export function RequestNewIntegrationFormDialog({ close, ...dialogProps }: RequestNewIntegrationFormDialogProps) {
  const [formSuccess, setFormSuccess] = useState(false)

  const formik = useFormik({
    validationSchema,
    initialValues,
    onSubmit: (values) => {
      console.log(`submitted values:`, values)
      ;(integrationsApi as unknown as { requestIntegration: (data: Record<string, unknown>) => void }).requestIntegration({
        ...values,
        appWebsite: addHttpsTransform(undefined, values.appWebsite),
        appApiDocumentation: values.appApiDocumentation ? addHttpsTransform(undefined, values.appApiDocumentation) : null,
        budget: parseFloat(`${values.budget}`),
      })
      setFormSuccess(true)
    },
  })

  function getFieldHelperText(fieldName: FieldName): string | undefined {
    const field = fields[fieldName]

    let error = ''
    if (formik.touched[fieldName] && formik.errors[fieldName]) {
      if (lodash.isArray(formik.errors[fieldName])) {
        error = (formik.errors[fieldName] as string[]).join(', ')
      } else {
        error = formik.errors[fieldName] as string
      }
    }

    return error || (field as unknown as { meta?: { helperText?: string } }).meta?.helperText
  }

  useEffect(() => {
    console.log('formik.touched:', formik.touched)
  }, [formik.touched])
  useEffect(() => {
    console.log('formik.errors:', formik.errors)
  }, [formik.errors])

  const formRef = useRef(undefined)
  // useEffect(() => {
  //   console.log('formRef.current:', formRef.current)
  // }, [formRef.current])

  return (
    <Dialog TransitionComponent={Transition} {...dialogProps}>
      <DialogTitle>Request New Integration</DialogTitle>
      <DialogContent>
        <Box maxWidth={'sx'}>
          {formSuccess ? (
            <Typography variant="h6" color="primary">
              Thanks for your request! We'll contact you shortly.
            </Typography>
          ) : (
            <Box
              ref={formRef}
              component="form"
              sx={{
                '> *': {
                  marginBottom: 2,
                  // margin: 0.5,
                },
                // padding: -0.5,
              }}
              onSubmit={formik.handleSubmit}
            >
              <Typography>Contact Info</Typography>
              <Stack direction="row">
                <TextField
                  sx={{ marginRight: 1 }}
                  id="firstName"
                  fullWidth
                  helperText={formik.touched['firstName'] && formik.errors['firstName'] ? formik.errors['firstName'] : ''}
                  error={!!formik.touched.firstName && !!formik.errors.firstName}
                  required={getFieldRequired('firstName')}
                  name="firstName"
                  label={getFieldLabel('firstName')}
                  type={getFieldType('firstName')}
                  value={formik.values['firstName']}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                <TextField
                  id="lastName"
                  fullWidth
                  helperText={getFieldHelperText('lastName')}
                  error={!!formik.touched.lastName && !!formik.errors.lastName}
                  required={getFieldRequired('lastName')}
                  name="lastName"
                  label={getFieldLabel('lastName')}
                  type={getFieldType('lastName')}
                  value={formik.values['lastName']}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
              </Stack>
              <TextField
                id="email"
                fullWidth
                helperText={getFieldHelperText('email')}
                error={!!formik.touched.email && !!formik.errors.email}
                required={getFieldRequired('email')}
                name="email"
                label={getFieldLabel('email')}
                type={getFieldType('email')}
                value={formik.values['email']}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              <TextField
                id="phone"
                fullWidth
                helperText={getFieldHelperText('phone')}
                error={!!formik.touched.phone && !!formik.errors.phone}
                required={getFieldRequired('phone')}
                name="phone"
                label={getFieldLabel('phone')}
                type={getFieldType('phone')}
                value={formik.values['phone']}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              <TextField
                id="companyName"
                fullWidth
                helperText={getFieldHelperText('companyName')}
                error={!!formik.touched.companyName && !!formik.errors.companyName}
                required={getFieldRequired('companyName')}
                name="companyName"
                label={getFieldLabel('companyName')}
                type={getFieldType('companyName')}
                value={formik.values['companyName']}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              <TextField
                id="position"
                fullWidth
                helperText={getFieldHelperText('position')}
                error={!!formik.touched.position && !!formik.errors.position}
                required={getFieldRequired('position')}
                name="position"
                label={getFieldLabel('position')}
                type={getFieldType('position')}
                value={formik.values['position']}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />

              <Divider />
              <Typography>Integration Details</Typography>
              <TextField
                id="appName"
                fullWidth
                helperText={getFieldHelperText('appName')}
                error={!!formik.touched.appName && !!formik.errors.appName}
                required={getFieldRequired('appName')}
                name="appName"
                label={getFieldLabel('appName')}
                type={getFieldType('appName')}
                value={formik.values['appName']}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              <TextField
                id="appDescription"
                fullWidth
                helperText={getFieldHelperText('appDescription')}
                error={!!formik.touched.appDescription && !!formik.errors.appDescription}
                required={getFieldRequired('appDescription')}
                name="appDescription"
                label={getFieldLabel('appDescription')}
                type={getFieldType('appDescription')}
                value={formik.values['appDescription']}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              <TextField
                id="appWebsite"
                fullWidth
                helperText={getFieldHelperText('appWebsite')}
                error={!!formik.touched.appWebsite && !!formik.errors.appWebsite}
                required={getFieldRequired('appWebsite')}
                name="appWebsite"
                label={getFieldLabel('appWebsite')}
                type={getFieldType('appWebsite')}
                value={formik.values['appWebsite']}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              <TextField
                id="appHasApi"
                fullWidth
                select
                helperText={getFieldHelperText('appHasApi')}
                error={!!formik.touched.appHasApi && !!formik.errors.appHasApi}
                required={getFieldRequired('appHasApi')}
                name="appHasApi"
                label={getFieldLabel('appHasApi')}
                type={getFieldType('appHasApi')}
                value={formik.values['appHasApi']}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              >
                {APP_HAS_API_OPTIONS.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                id="appApiDocumentation"
                fullWidth
                helperText={getFieldHelperText('appApiDocumentation')}
                error={!!formik.touched.appApiDocumentation && !!formik.errors.appApiDocumentation}
                required={getFieldRequired('appApiDocumentation')}
                name="appApiDocumentation"
                label={getFieldLabel('appApiDocumentation')}
                type={getFieldType('appApiDocumentation')}
                value={formik.values['appApiDocumentation']}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              <TextField
                id="integrationType"
                fullWidth
                select
                helperText={getFieldHelperText('integrationType')}
                error={!!formik.touched.integrationType && !!formik.errors.integrationType}
                required={getFieldRequired('integrationType')}
                name="integrationType"
                label={getFieldLabel('integrationType')}
                type={getFieldType('integrationType')}
                value={formik.values['integrationType']}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              >
                {COMMON_INTEGRATION_TYPES.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                fullWidth
                multiline
                id="integrationDetails"
                minRows={3}
                helperText={getFieldHelperText('integrationDetails')}
                error={!!formik.touched.integrationDetails && !!formik.errors.integrationDetails}
                required={getFieldRequired('integrationDetails')}
                name="integrationDetails"
                label={getFieldLabel('integrationDetails')}
                type={getFieldType('integrationDetails')}
                value={formik.values['integrationDetails']}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              <TextField
                fullWidth
                id="desiredTimeline"
                helperText={getFieldHelperText('desiredTimeline')}
                error={!!formik.touched.desiredTimeline && !!formik.errors.desiredTimeline}
                required={getFieldRequired('desiredTimeline')}
                name="desiredTimeline"
                label={getFieldLabel('desiredTimeline')}
                type={getFieldType('desiredTimeline')}
                value={formik.values['desiredTimeline']}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              <NumericFormat
                id="budget"
                fullWidth
                helperText={getFieldHelperText('budget')}
                error={!!formik.touched.budget && !!formik.errors.budget}
                required={getFieldRequired('budget')}
                name="budget"
                label={getFieldLabel('budget')}
                value={formik.values['budget']}
                onBlur={formik.handleBlur}
                customInput={TextField}
                prefix={'$'}
                thousandSeparator
                onValueChange={({ value }) => formik.setFieldValue('budget', value)}
              />

              <TextField
                fullWidth
                multiline
                id="additionalComments"
                minRows={3}
                helperText={getFieldHelperText('additionalComments')}
                error={!!formik.touched.additionalComments && !!formik.errors.additionalComments}
                required={getFieldRequired('additionalComments')}
                name="additionalComments"
                label={getFieldLabel('additionalComments')}
                type={getFieldType('additionalComments')}
                value={formik.values['additionalComments']}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        {formSuccess ? (
          <>
            <Button
              onClick={() => {
                close()
                formik.resetForm()
                setFormSuccess(false)
              }}
            >
              Close
            </Button>
            <Button
              onClick={() => {
                formik.resetForm()
                setFormSuccess(false)
              }}
            >
              Request Another Integration
            </Button>
          </>
        ) : (
          <>
            <Button onClick={close}>Cancel</Button>
            <Button onClick={formik.submitForm}>Submit</Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  )
}
